import React, { useState, useEffect, useRef } from 'react';
import CommonModal from '../../components/commonComponent/CommonModal';
import CommonTable from '../../components/commonComponent/CommanTable';
import UserFilters from '../../components/commonComponent/CommanFilters';
import { validateForm } from '../../utils/formUtils';
import { FaDownload, FaPencilAlt, FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { hasPermission } from '../../utils/permissionHelper';

function ViewPlanFeaturesModal({ visible, billing, onClose }) {
  if (!visible || !billing) return null;
  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        background: "rgba(0,0,0,0.2)",
        zIndex: 1050,
      }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content p-3">
          <div className="modal-header border-0 pb-0">
            <h4 className="modal-title fw-bold">
              <FaEye className="me-2" /> View Plan Features
            </h4>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body border-radius">
            {/* Basic Information */}
            <div className="mb-2">
              <div className="fw-bold">Basic Information</div>
              <div className="text-dark">
                Invoice No. : {billing.invoiceId || billing.invoiceNumber || billing._id || 'N/A'}
                <br />
                {billing.companyName || billing.company || 'N/A'}
              </div>
            </div>
            <hr />
            {/* Payment Information */}
            <div className="mb-2">
              <div className="fw-bold">Payment Information</div>
              <div className="row">
                <div className="col-6">
                  <div className="text-dark">
                    Payment Date
                  </div>
                  <div>
                    <span role="img" aria-label="calendar">:spiral_calendar_pad:</span> {billing.paymentDate || billing.date || billing.createdAt ? new Date(billing.createdAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-dark">
                    Next Payment Date
                  </div>
                  <div>
                    <span role="img" aria-label="calendar">:spiral_calendar_pad:</span> {billing.nextDate || billing.nextPaymentDate || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            {/* Business Information */}
            <div className="mb-2">
              <div className="fw-bold">Business Information</div>
              <div className="row">
                <div className="col-6">
                  <div className="text-dark">
                    GST Number
                  </div>
                  <div>
                    <span role="img" aria-label="gst">:page_facing_up:</span> {billing.gstNumber || 'N/A'}
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-dark">
                    Transaction Id
                  </div>
                  <div>
                    <span role="img" aria-label="tid">:page_facing_up:</span> {billing.TransactionID || billing.transactionId || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            {/* Plan & Package Information */}
            <div className="mb-2">
              <div className="fw-bold">Plan & Package Information</div>
              <div className="row">
                <div className="col-6">
                  <div className="text-dark">
                    Package Types
                  </div>
                  <div>
                    <span role="img" aria-label="plan">:package:</span> {
                      typeof billing.plan === 'object'
                        ? billing.plan?.name || 'N/A'
                        : billing.package || billing.plan || 'N/A'
                    }
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-dark">
                    Amount
                  </div>
                  <div>
                    <span role="img" aria-label="amount">:moneybag:</span> {billing.finalPrice || billing.amount || billing.price || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            {/* Quick Information */}
            <div className="mb-2">
              <div className="fw-bold">Quick Information</div>
              <div className="row gx-2">
                <div className="col-6">
                  <div>
                    <div className="text-dark">User Name</div>
                    <div className="text-bold">
                      {
                        typeof billing.user === 'object'
                          ? `${billing.user?.firstName || ''} ${billing.user?.lastName || ''}`.trim() || billing.user?.email || 'N/A'
                          : billing.userName || (billing.firstName && billing.lastName ? `${billing.firstName} ${billing.lastName}` : 'N/A')
                      }
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div>
                    <div className="text-dark">Company</div>
                    <div className="text-bold">
                      {
                        typeof billing.user === 'object'
                          ? billing.user?.companyName || billing.companyName || billing.company || 'N/A'
                          : billing.companyName || billing.company || 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-4">
              <button type="button" className="btn btn-light" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const allFields = [
  { key: 'srNo', label: 'Sr.No.' },
  { key: 'billingNumber', label: 'Billing Number' },
  { key: 'userName', label: 'User Name' },
  { key: 'planName', label: 'Plan' },
  { key: 'price', label: 'Price' },
  { key: 'finalPrice', label: 'Final Price' },
  { key: 'duration', label: 'Duration (Months)' },
  { key: 'gstPercentage', label: 'GST (%)' },
  { key: 'discount', label: 'Discount (%)' },
  { key: 'createdAt', label: 'Created Date' },
];

const billingFields = [
  { type: "heading", label: "Invoice Details" },
  { name: "user", label: "Select User", type: "select", options: [], placeholder: "Select User" },
  { type: "heading", label: "Plan & Payment Method" },
  { name: "plan", label: "Select Plan", type: "select", options: [], placeholder: "Select Plan" },
  { name: "price", label: "Price", type: "text", placeholder: "Enter price", readonly: true },
  { name: "duration", label: "Duration (Months)", type: "text", placeholder: "Enter duration in months", readonly: true },
  { name: "gstPercentage", label: "GST (%)", type: "text", placeholder: "Enter GST percentage", readonly: true },
  { name: "discount", label: "Discount (%)", type: "text", placeholder: "Enter discount percentage" },
  { name: "finalPrice", label: "Final Price", type: "text", placeholder: "Final price will be calculated", readonly: true },
];

const initialBillingValues = {
  user: "",
  plan: "",
  price: "",
  duration: "",
  gstPercentage: "",
  discount: "",
  finalPrice: "",
};

function BillingIndex() {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(initialBillingValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billings, setBillings] = useState([]);
  const [filteredBillings, setFilteredBillings] = useState([]);
    const [page, setPage] = useState(1);
    const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
    const [limit, setLimit] = useState(defaultLimit);
  const [totalBillings, setTotalBillings] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Permission
  const [userObj, setUserObj] = useState(null);
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) setUserObj(JSON.parse(storedUser));
    } catch {}
  }, []);
  const canListBilling = hasPermission(userObj, 'Billing', 'List');
  const canViewBilling = hasPermission(userObj, 'Billing', 'View');
  const canCreateBilling = hasPermission(userObj, 'Billing', 'Create');
  const canEditBilling = hasPermission(userObj, 'Billing', 'Edit');
  const canDownloadBilling = hasPermission(userObj, 'Billing', 'Download');
  const canExportBilling = hasPermission(userObj, 'Billing', 'Export');

  const forcedFields = ['srNo'];
  const getInitialVisibleFields = () => {
    const stored = localStorage.getItem('billing_visible_columns');
    return stored ? JSON.parse(stored) : allFields.map(f => f.key);
  };
  const [visibleFields, setVisibleFields] = useState(getInitialVisibleFields);

  useEffect(() => {
    localStorage.setItem('billing_visible_columns', JSON.stringify(visibleFields));
  }, [visibleFields]);

  useEffect(() => {
    if (!showColumnDropdown) return;
    function handleClickOutside(event) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnDropdown]);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      await fetchUsers();
      await fetchPlans();
      fetchBillings(page, limit);
    };
    fetchData();
    // eslint-disable-next-line
  }, [canListBilling, canViewBilling]);

  useEffect(() => {
    // refetch when pagination changes
    fetchBillings(page, limit);
    // eslint-disable-next-line
  }, [page, limit]);

  useEffect(() => {
    applyFilters(billings);
  }, [searchTerm, filters, billings]);

  const fetchBillings = async (pageArg = 1, limitArg = 10) => {
    if (!canListBilling && !canViewBilling) {
      setBillings([]); setFilteredBillings([]);
      return;
    }
    try {
      const response = await axios.get(`/api/billing?page=${pageArg}&limit=${limitArg}`);
      if (response.data && response.data.Status === 1) {
        const bills = response.data.billings || response.data.data || [];
        setBillings(bills);
        setFilteredBillings(bills);
        const total = response.data.total || response.data.count || response.data.totalItems || response.data.totalCount || bills.length;
        setTotalBillings(Number(total));
      } else {
        toastError('Failed to fetch billings');
      }
    } catch (error) {
      toastError('Failed to fetch billings');
    }
  };

  const fetchPlans = async () => {
    try {
      const userHasPlan = localStorage.getItem("userHasPlan") === "true";
      const defaultParam = userHasPlan ? 'false' : 'true';
      const response = await axios.get(`/api/plan?default=${defaultParam}`);
      if (response.data && response.data.Status === 1) {
        const planOptions = response.data.plans.map(plan => ({ value: plan._id, label: plan.name }));
        setPlans(planOptions);
      }
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const userType = "Company";
      const response = await axios.get(`/api/user?userType=${userType}`);
      if (response.data && response.data.Status === 1) {
        const currentUserId = localStorage.getItem("userId");
        const currentUser = response.data.users.find(user => user._id === currentUserId);
        if (currentUser) {
          localStorage.setItem("userHasPlan", currentUser.plan ? "true" : "false");
        }
        setUsers(
          response.data.users.map(user => ({
            value: user._id,
            label:
              (user.firstName || "") + " " + (user.lastName || "") ||
              user.email
          }))
        );
      }
    } catch {}
  };

  const dynamicBillingFields = billingFields.map(field => {
    if (field.name === "plan") return { ...field, options: plans };
    if (field.name === "user") return { ...field, options: users };
    return field;
  });

  const tableColumns = allFields
    .filter(field => forcedFields.includes(field.key) || visibleFields.includes(field.key))
    .map(field => ({ key: field.key, label: field.label }))
    .concat([{ key: 'actions', label: 'Action' }]);

  const tableData = filteredBillings.map((billing, idx) => ({
    ...billing,
    srNo: idx + 1,
    billingNumber: billing.billingNumber || `BILL-${idx + 1}`,
    invoiceId: billing.billingNumber || billing._id || `INV-${idx + 1}`,
    userName: typeof billing.user === 'object' ?
      `${billing.user?.firstName || ''} ${billing.user?.lastName || ''}`.trim() || billing.user?.email || 'N/A' :
      'N/A',
    planName: typeof billing.plan === 'object' ? billing.plan?.name : 'N/A',
    createdAt: billing.createdAt ? new Date(billing.createdAt).toLocaleDateString() : 'N/A',
    actions: (
      <div>
        {canDownloadBilling && (
          <button
            className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Download"
            onClick={() => handleDownload(billing)}
          >
            <FaDownload size={14} />
          </button>
        )}
        {canViewBilling && (
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="View"
            onClick={() => {
              setSelectedBilling(billing);
              setShowPlanModal(true);
            }}
          >
            <FaEye size={14} />
          </button>
        )}
        {canEditBilling && (
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => handleEdit(billing)}
          >
            <FaPencilAlt size={14} />
          </button>
        )}
      </div>
    ),
  }));

  const handleAdd = () => {
    if (!canCreateBilling) {
      toastError("You do not have permission to create billings.");
      return;
    }
    setModalData(initialBillingValues);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (billing) => {
    if (!canEditBilling) {
      toastError("You do not have permission to edit billings.");
      return;
    }
    setModalData({
      ...billing,
      user: typeof billing.user === 'object' ? billing.user?._id : billing.user,
      plan: typeof billing.plan === 'object' ? billing.plan?._id : billing.plan,
    });
    setErrors({});
    setShowModal(true);
  };

  const handlePlanChange = async (planId) => {
    if (!planId) {
      setModalData(prev => ({
        ...prev,
        plan: "",
        price: "",
        duration: "",
        gstPercentage: "",
        finalPrice: ""
      }));
      return;
    }
    try {
      const response = await axios.get(`/api/plan/${planId}`);
      if (response.data && response.data.Status === 1) {
        const planData = response.data.plan;
        const basePrice = parseFloat(planData.price || 0);
        const gstPercentage = parseFloat(planData.gstPercentage || 0);
        const discount = parseFloat(modalData.discount || 0);
        const gstAmount = (basePrice * gstPercentage) / 100;
        const discountAmount = (basePrice * discount) / 100;
        const finalPrice = basePrice + gstAmount - discountAmount;
        setModalData(prev => ({
          ...prev,
          plan: planId,
          price: planData.price || "",
          duration: planData.duration || "",
          gstPercentage: planData.gstPercentage || "",
          finalPrice: finalPrice.toFixed(2)
        }));
      }
    } catch {}
  };

  const calculateFinalPrice = (price, gstPercentage, discount) => {
    const basePrice = parseFloat(price || 0);
    const gst = parseFloat(gstPercentage || 0);
    const disc = parseFloat(discount || 0);
    const gstAmount = (basePrice * gst) / 100;
    const discountAmount = (basePrice * disc) / 100;
    const finalPrice = basePrice + gstAmount - discountAmount;
    return finalPrice.toFixed(2);
  };

  const handleModalChange = (updatedValues) => {
    if (typeof updatedValues === 'object' && updatedValues !== null) {
      if (updatedValues.plan !== modalData.plan && updatedValues.plan) {
        handlePlanChange(updatedValues.plan);
        return;
      }
      if (updatedValues.price !== modalData.price ||
        updatedValues.gstPercentage !== modalData.gstPercentage ||
        updatedValues.discount !== modalData.discount) {
        updatedValues.finalPrice = calculateFinalPrice(
          updatedValues.price,
          updatedValues.gstPercentage,
          updatedValues.discount
        );
      }
      setModalData(updatedValues);
    } else {
      const field = arguments[0];
      const value = arguments[1];
      if (field === 'plan') {
        handlePlanChange(value);
        return;
      }
      const updatedData = { ...modalData, [field]: value };
      if (['price', 'gstPercentage', 'discount'].includes(field)) {
        updatedData.finalPrice = calculateFinalPrice(
          updatedData.price,
          updatedValues.gstPercentage,
          updatedValues.discount
        );
      }
      setModalData(updatedData);
    }
  };

  const handleDownload = (billing) => {
    if (!canDownloadBilling) {
      toastError("You do not have permission to download billings.");
      return;
    }
    toastSuccess('Download feature will be implemented');
  };

  const handleExportExcel = async () => {
    if (!canExportBilling) {
      toastError("You do not have permission to export billings.");
      return;
    }
    try {
      setIsExporting(true);
      const businessId = localStorage.getItem('businessId');
      let url = '/api/billing/export-excel';
      if (businessId) {
        url += `?businessId=${businessId}`;
      }
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      const url2 = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url2;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'billings_export.xlsx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          filename = fileNameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url2);
      toastSuccess('Billing data exported successfully!');
    } catch (error) {
      toastError('Failed to export billing data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const validationRules = {
    user: { required: true },
    plan: { required: true },
    price: { required: true },
    duration: { required: true },
    gstPercentage: { required: true },
    finalPrice: { required: true },
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(modalData, validationRules);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        user: modalData.user,
        plan: modalData.plan,
        price: Number(modalData.price),
        duration: Number(modalData.duration),
        gstPercentage: Number(modalData.gstPercentage),
        discount: Number(modalData.discount || 0),
        finalPrice: Number(modalData.finalPrice),
      };
      let response;
      if (modalData._id) {
        response = await axios.put(`/api/billing/${modalData._id}`, payload);
      } else {
        response = await axios.post('/api/billing', payload);
      }
      if (response.data && response.data.Status === 1) {
        toastSuccess(modalData._id ? 'Billing updated successfully!' : 'Billing added successfully!');
        fetchBillings();
        setShowModal(false);
        setModalData(initialBillingValues);
        setErrors({});
      } else {
        toastError(response.data.Message || 'Failed to save billing');
      }
    } catch (error) {
      toastError(error.response?.data?.Message || 'Failed to save billing');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(billings, newFilters);
  };

  const handleClearFilters = () => {
    setFilters({ startDate: '', endDate: '' });
    setFilteredBillings(billings);
  };

  const applyFilters = (dataToFilter = billings, filtersToApply = filters) => {
    let filtered = [...dataToFilter];
    if (searchTerm) {
      filtered = filtered.filter(billing =>
        (typeof billing.plan === 'object' ? billing.plan?.name : '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof billing.user === 'object' ? `${billing.user?.firstName} ${billing.user?.lastName}` : '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        billing.price?.toString().includes(searchTerm) ||
        billing.billingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        billing._id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filtersToApply.startDate) {
      const start = new Date(filtersToApply.startDate);
      filtered = filtered.filter(billing => {
        const created = new Date(billing.createdAt);
        return created >= start;
      });
    }
    if (filtersToApply.endDate) {
      const end = new Date(filtersToApply.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(billing => {
        const created = new Date(billing.createdAt);
        return created <= end;
      });
    }
    setFilteredBillings(filtered);
  };

  return (
    <>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
          <h2 className="h2 mb-0 text-dark">Billings</h2>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-light"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="fas fa-filter me-1"></i> Filter
            </button>
            {canExportBilling && (
              <button
                className="btn btn-light"
                onClick={handleExportExcel}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download me-1"></i> Export Excel
                  </>
                )}
              </button>
            )}
            {canCreateBilling && (
              <button
                className="btn btn-primary"
                onClick={handleAdd}
              >
                <i className="fas fa-plus me-1"></i> Add New Bill
              </button>
            )}
            <div style={{ position: 'relative' }} ref={columnDropdownRef}>
              {showColumnDropdown && (
                <div
                  className="dropdown-menu show p-3"
                  style={{
                    maxHeight: 250,
                    overflowY: 'auto',
                    right: 0,
                    left: 'auto',
                    position: 'absolute',
                    zIndex: 1050,
                    width: 'max-content',
                    minWidth: 220,
                  }}
                >
                  {allFields.map(col => (
                    <div key={col.key} className="form-check mb-1">
                      <input
                        type="checkbox"
                        id={`col-filter-${col.key}`}
                        className="form-check-input"
                        checked={visibleFields.includes(col.key) || forcedFields.includes(col.key)}
                        disabled={forcedFields.includes(col.key)}
                        onChange={e => {
                          if (e.target.checked) {
                            setVisibleFields(prev => {
                              const allKeys = allFields.map(c => c.key);
                              const newCols = [...prev, col.key];
                              const uniqueCols = Array.from(new Set(newCols));
                              uniqueCols.sort((a, b) => allKeys.indexOf(a) - allKeys.indexOf(b));
                              return uniqueCols;
                            });
                          } else {
                            setVisibleFields(prev => prev.filter(k => k !== col.key));
                          }
                        }}
                      />
                      <label htmlFor={`col-filter-${col.key}`} className="form-check-label">
                        {col.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {showFilters && (
          <div className="mb-4 d-flex justify-content-center">
            <div style={{ width: '100%', maxWidth: 760, boxSizing: 'border-box', padding: '0 16px' }}>
              <UserFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                fields={['dateRange']}
              />
            </div>
          </div>
        )}
        <div className="row mb-4">
          <div className="col-md-6"></div>
          <div className="col-md-6 d-flex justify-content-end">
            <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
              <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Type to search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
              />
            </div>
          </div>
        </div>
        <CommonTable
          columns={tableColumns}
          data={tableData}
          itemsPerPageOptions={[10, 15, 25, 50]}
          defaultItemsPerPage={10}
          page={page}
          onPageChange={setPage}
          itemsPerPage={limit}
          onItemsPerPageChange={setLimit}
          totalItems={totalBillings}
        />
        <CommonModal
          visible={showModal}
          title={modalData && modalData._id ? "Edit Bill" : "Add New Bill"}
          fields={dynamicBillingFields}
          values={modalData}
          onChange={handleModalChange}
          onCancel={() => {
            setShowModal(false);
            setModalData(initialBillingValues);
            setErrors({});
          }}
          onSubmit={handleSubmit}
          submitLabel={isSubmitting ? (modalData && modalData._id ? "Updating..." : "Saving...") : (modalData && modalData._id ? "Update" : "Save")}
          cancelLabel="Cancel"
          requiredFields={[
            "billingNumber",
            "user",
            "plan",
            "price",
            "duration",
            "gstPercentage",
            "finalPrice",
          ]}
          errors={errors}
          disabled={isSubmitting}
        />
        <ViewPlanFeaturesModal
          visible={showPlanModal}
          billing={selectedBilling}
          onClose={() => setShowPlanModal(false)}
        />
      </div>
    </>
  );
}

export default BillingIndex;